import { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText, Check, X, Loader2, Eye, Ban, AlertTriangle, Package,
  Plus, Briefcase, Building2, ShoppingCart, Cog, Search, Filter, PackagePlus, Trash2, Undo2, Banknote,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDateTime } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface NF {
  id: number
  status: string
  numero_nf: string | null
  chave_acesso: string | null
  data_emissao: string | null
  data_entrada: string | null
  fornecedor_id: number | null
  fornecedor_nome: string | null
  fornecedor_cnpj: string | null
  fornecedor_razao: string | null
  deposito_id: number | null
  compra_pedido_id: number | null
  origem_venda_pedido_id: number | null
  valor_produtos: number
  valor_total: number
  tipo_operacao: string
  observacoes: string | null
  total_itens: number
}

const STATUS_COR: Record<string, string> = {
  rascunho: 'bg-ink-200 text-ink-700',
  pre_entrada: 'bg-sky-100 text-sky-800',
  conferencia: 'bg-amber-100 text-amber-800',
  confirmada: 'bg-emerald-100 text-emerald-800',
  cancelada: 'bg-rose-100 text-rose-700',
}

export default function NFEntradasPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [status, setStatus] = useState<string>('')
  const [detalhe, setDetalhe] = useState<NF | null>(null)
  const [tipoNova, setTipoNova] = useState<'produto' | 'imobilizado' | 'consumo' | 'servico' | 'avulsa' | null>(null)
  const [busca, setBusca] = useState('')
  const [fornecedorFiltro, setFornecedorFiltro] = useState<string>('')
  const [tipoFiltro, setTipoFiltro] = useState<string>('')
  const [dataDe, setDataDe] = useState('')
  const [dataAte, setDataAte] = useState('')
  const [intercompanyFiltro, setIntercompanyFiltro] = useState<string>('')
  const [valorMin, setValorMin] = useState('')
  const [valorMax, setValorMax] = useState('')

  const { data: nfs = [], isLoading } = useQuery<NF[]>({
    queryKey: ['nf-entradas', status],
    queryFn: async () => (await api.get('/compras/nf-entradas', { params: { status: status || undefined } })).data,
  })

  const { data: fornecedoresNF = [] } = useQuery<any[]>({
    queryKey: ['fornecedores-nf'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'fornecedor', limit: 500 } })).data,
  })

  const nfsFiltradas = useMemo(() => {
    return nfs.filter(nf => {
      // Cancelada some da lista por padrão (a reimportada substitui visualmente);
      // só aparece quando o filtro de status é explicitamente "cancelada".
      if (status !== 'cancelada' && nf.status === 'cancelada') return false
      if (busca) {
        const t = busca.toLowerCase()
        const m = (nf.numero_nf || '').toLowerCase().includes(t)
          || (nf.fornecedor_nome || '').toLowerCase().includes(t)
          || (nf.chave_acesso || '').toLowerCase().includes(t)
          || String(nf.id).includes(t)
        if (!m) return false
      }
      if (fornecedorFiltro && nf.fornecedor_id !== Number(fornecedorFiltro)) return false
      if (tipoFiltro && nf.tipo_operacao !== tipoFiltro) return false
      if (dataDe && (!nf.data_emissao || nf.data_emissao.slice(0, 10) < dataDe)) return false
      if (dataAte && (!nf.data_emissao || nf.data_emissao.slice(0, 10) > dataAte)) return false
      if (intercompanyFiltro === 'sim' && !nf.origem_venda_pedido_id) return false
      if (intercompanyFiltro === 'nao' && nf.origem_venda_pedido_id) return false
      if (valorMin && Number(nf.valor_total) < Number(valorMin)) return false
      if (valorMax && Number(nf.valor_total) > Number(valorMax)) return false
      return true
    })
  }, [nfs, status, busca, fornecedorFiltro, tipoFiltro, dataDe, dataAte, intercompanyFiltro, valorMin, valorMax])

  const totalNFFiltrado = useMemo(
    () => nfsFiltradas.reduce((s, n) => s + Number(n.valor_total || 0), 0),
    [nfsFiltradas]
  )

  const limparFiltros = () => {
    setBusca(''); setFornecedorFiltro(''); setTipoFiltro(''); setDataDe(''); setDataAte('')
    setIntercompanyFiltro(''); setValorMin(''); setValorMax('')
  }
  const filtrosAtivos = (busca ? 1 : 0) + (fornecedorFiltro ? 1 : 0) + (tipoFiltro ? 1 : 0)
    + (dataDe ? 1 : 0) + (dataAte ? 1 : 0) + (intercompanyFiltro ? 1 : 0)
    + (valorMin ? 1 : 0) + (valorMax ? 1 : 0)

  const { data: contagem } = useQuery<Record<string, number>>({
    queryKey: ['nf-contagem'],
    queryFn: async () => {
      const d = (await api.get('/compras/nf-entradas')).data as NF[]
      const c: Record<string, number> = {}
      for (const n of d) c[n.status] = (c[n.status] || 0) + 1
      return c
    },
  })

  const confirmar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/compras/nf-entradas/${id}/confirmar`)).data,
    onSuccess: (_d, id) => {
      const nf = (nfs || []).find((n: any) => n.id === id)
      const ref = nf?.numero_nf || String(id)
      toast.success(
        (t) => (
          <span className="flex items-center gap-2">
            NF confirmada — estoque e contas a pagar gerados
            <button
              onClick={() => { toast.dismiss(t.id); navigate(`/financeiro/pagar?q=NF ${ref}&status=`) }}
              className="underline font-semibold whitespace-nowrap">
              Ver contas a pagar →
            </button>
          </span>
        ),
        { duration: 8000 },
      )
      qc.invalidateQueries({ queryKey: ['nf-entradas'] })
      qc.invalidateQueries({ queryKey: ['nf-contagem'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao confirmar NF'),
  })
  const cancelar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/compras/nf-entradas/${id}/cancelar`)).data,
    onSuccess: (_d, id) => {
      const era = (nfs || []).find((n: any) => n.id === id)
      toast.success(era?.status === 'confirmada' ? 'NF estornada — estoque devolvido e contas a pagar canceladas' : 'NF cancelada')
      qc.invalidateQueries({ queryKey: ['nf-entradas'] })
      qc.invalidateQueries({ queryKey: ['nf-contagem'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar'),
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/compras/nf-entradas/${id}`)).data,
    onSuccess: () => {
      toast.success('NF excluída')
      qc.invalidateQueries({ queryKey: ['nf-entradas'] })
      qc.invalidateQueries({ queryKey: ['nf-contagem'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  const preEntradas = contagem?.pre_entrada || 0

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="text-brand-600" /> NFs de Entrada
          </h1>
          <p className="text-ink-600 text-sm">Recebimento de mercadorias. Confirma estoque e gera contas a pagar.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Link to="/compras" className="btn-secondary text-sm">← Compras</Link>
          <ImportarXMLBotao onDone={() => qc.invalidateQueries({ queryKey: ['nf-entradas'] })} />
          <NovaNFDropdown onPick={setTipoNova} />
        </div>
      </div>

      {preEntradas > 0 && (
        <div className="card card-body bg-sky-50 border-sky-200 flex items-center gap-3">
          <AlertTriangle className="text-sky-600 shrink-0" size={20} />
          <div className="flex-1 text-sm text-sky-900">
            <strong>{preEntradas}</strong> pré-entrada(s) aguardando conferência (recebidas automaticamente de empresas do grupo).
          </div>
          <button onClick={() => setStatus('pre_entrada')} className="btn-secondary !py-1 text-xs">Ver</button>
        </div>
      )}

      <div className="card card-body space-y-3">
        {/* Linha 1: busca + status */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-9 !py-1.5 text-sm" placeholder="Buscar por nº NF, fornecedor, chave..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={enterAbrePrimeiro(nfsFiltradas, nf => setDetalhe(nf))} />
          </div>
          <span className="text-ink-600 text-xs">Status:</span>
          {['', 'rascunho', 'pre_entrada', 'conferencia', 'confirmada', 'cancelada'].map(s => (
            <button key={s || 'todos'} onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded text-xs flex items-center gap-1 ${status === s ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700'}`}>
              {s === '' ? 'Todos' : s.replace('_', ' ')}
              {s && contagem?.[s] ? (
                <span className={`text-[10px] ${status === s ? 'bg-white text-brand-700' : 'bg-ink-300'} px-1 rounded`}>
                  {contagem[s]}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        {/* Linha 2: filtros avançados */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-ink-100">
          <Filter size={12} className="text-ink-400" />
          <select className="input !py-1 !text-xs w-auto min-w-[160px]"
            value={fornecedorFiltro} onChange={e => setFornecedorFiltro(e.target.value)}>
            <option value="">Todos fornecedores</option>
            {fornecedoresNF.map((f: any) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
          <select className="input !py-1 !text-xs w-auto"
            value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)}>
            <option value="">Todos os tipos</option>
            <option value="produto">Produto</option>
            <option value="imobilizado">Imobilizado</option>
            <option value="consumo">Uso/Consumo</option>
            <option value="servico">Serviço</option>
          </select>
          <div className="flex items-center gap-1 text-xs text-ink-600">
            Emissão:
            <input type="date" className="input !py-1 !text-xs w-auto" value={dataDe} onChange={e => setDataDe(e.target.value)} />
            <span>até</span>
            <input type="date" className="input !py-1 !text-xs w-auto" value={dataAte} onChange={e => setDataAte(e.target.value)} />
          </div>
          <select className="input !py-1 !text-xs w-auto"
            value={intercompanyFiltro} onChange={e => setIntercompanyFiltro(e.target.value)}>
            <option value="">Todos (intercompany?)</option>
            <option value="sim">Apenas intercompany</option>
            <option value="nao">Sem intercompany</option>
          </select>
          <div className="flex items-center gap-1 text-xs text-ink-600">
            Valor:
            <input type="number" placeholder="min" className="input !py-1 !text-xs w-20"
              value={valorMin} onChange={e => setValorMin(e.target.value)} />
            <span>até</span>
            <input type="number" placeholder="max" className="input !py-1 !text-xs w-24"
              value={valorMax} onChange={e => setValorMax(e.target.value)} />
          </div>
          {filtrosAtivos > 0 && (
            <button onClick={limparFiltros} className="text-xs text-rose-600 hover:underline ml-1">
              <X size={11} className="inline" /> Limpar ({filtrosAtivos})
            </button>
          )}
          <span className="ml-auto text-xs text-ink-500">
            {nfsFiltradas.length} de {nfs.length} · <strong className="text-ink-800">{formatMoney(totalNFFiltrado)}</strong>
          </span>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>#</th><th>NFe</th><th>Fornecedor</th><th>Emissão</th>
              <th className="text-right">Itens</th><th className="text-right">Total</th>
              <th>Status</th><th className="w-40"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && nfsFiltradas.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-ink-500">
                {nfs.length === 0 ? 'Nenhuma NF.' : 'Nenhuma NF corresponde aos filtros.'}
              </td></tr>
            )}
            {nfsFiltradas.map(nf => (
              <tr key={nf.id}
                onClick={() => setDetalhe(nf)}
                className="hover:bg-violet-50 cursor-pointer">
                <td className="font-mono">#{nf.id}</td>
                <td>
                  <div>{nf.numero_nf || '-'}</div>
                  {nf.origem_venda_pedido_id && (
                    <span className="text-[10px] bg-sky-100 text-sky-800 px-1 rounded">INTERCOMPANY</span>
                  )}
                </td>
                <td>{nf.fornecedor_nome || '-'}</td>
                <td className="text-xs">{formatDateTime(nf.data_emissao)}</td>
                <td className="text-right">{nf.total_itens}</td>
                <td className="text-right font-semibold">{formatMoney(nf.valor_total)}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COR[nf.status]}`}>{nf.status.replace('_', ' ')}</span></td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <button onClick={() => setDetalhe(nf)} className="btn-secondary !py-1 !px-2 text-xs">
                      <Eye size={12} /> Ver
                    </button>
                    {(nf.status === 'rascunho' || nf.status === 'pre_entrada' || nf.status === 'conferencia') && (
                      <>
                        <button onClick={() => confirmar.mutate(nf.id)} disabled={confirmar.isPending}
                          title="Confirmar (gera estoque + contas a pagar)"
                          className="btn-secondary !py-1 !px-2 text-xs text-emerald-700">
                          <Check size={12} />
                        </button>
                        <button onClick={() => cancelar.mutate(nf.id)} title="Cancelar"
                          className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                          <Ban size={12} />
                        </button>
                      </>
                    )}
                    {nf.status === 'confirmada' && (
                      <Link to={`/financeiro/pagar?q=NF ${nf.numero_nf || nf.id}&status=`}
                        title="Ver contas a pagar desta NF"
                        className="btn-secondary !py-1 !px-2 text-xs text-sky-700">
                        <Banknote size={12} />
                      </Link>
                    )}
                    {nf.status === 'confirmada' && (
                      <button title="Estornar (devolve estoque e cancela contas a pagar)"
                        onClick={() => { if (confirm(`Estornar a NF #${nf.id}?\n\nIsso vai DEVOLVER o estoque e CANCELAR as contas a pagar geradas.`)) cancelar.mutate(nf.id) }}
                        className="btn-secondary !py-1 !px-2 text-xs text-amber-700">
                        <Undo2 size={12} />
                      </button>
                    )}
                    <button title="Excluir nota"
                      onClick={() => pedirExclusao({ descricao: `a NF de entrada #${nf.id}`, onConfirm: () => excluir.mutate(nf.id) })}
                      className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detalhe && <DetalheNFModal nf={detalhe} onClose={() => setDetalhe(null)} />}
      {tipoNova && <NovaNFModal tipo={tipoNova} onClose={() => setTipoNova(null)}
        onDone={() => { setTipoNova(null); qc.invalidateQueries({ queryKey: ['nf-entradas'] }); qc.invalidateQueries({ queryKey: ['nf-contagem'] }) }} />}
    </div>
  )
}

function DetalheNFModal({ nf: nfInicial, onClose }: { nf: NF; onClose: () => void }) {
  const qc = useQueryClient()
  // Fonte de verdade reativa: refaz o GET do detalhe pra o modal atualizar sozinho
  // (fornecedor vinculado, depósito salvo) sem precisar fechar e reabrir.
  const { data: nf = nfInicial } = useQuery<NF>({
    queryKey: ['nf-detalhe', nfInicial.id],
    queryFn: async () => (await api.get(`/compras/nf-entradas/${nfInicial.id}`)).data,
    initialData: nfInicial,
  })
  const { data } = useQuery<{ itens: any[]; nao_mapeados: number }>({
    queryKey: ['nf-itens', nf.id],
    queryFn: async () => (await api.get(`/compras/nf-entradas/${nf.id}/itens`)).data,
  })
  const [showRateio, setShowRateio] = useState(false)
  const [lembrar, setLembrar] = useState(true)
  const editavel = !['confirmada', 'cancelada'].includes(nf.status)
  const naoMapeados = data?.nao_mapeados ?? 0
  const recarregar = () => {
    qc.invalidateQueries({ queryKey: ['nf-detalhe', nf.id] })
    qc.invalidateQueries({ queryKey: ['nf-itens', nf.id] })
    qc.invalidateQueries({ queryKey: ['nf-entradas'] })
  }
  const vincularFornecedor = useMutation({
    mutationFn: async () => (await api.post(`/compras/nf-entradas/${nf.id}/vincular-fornecedor`)).data,
    onSuccess: () => { toast.success('Fornecedor vinculado'); recarregar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao vincular fornecedor'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">NF #{nf.id} · {nf.numero_nf || '—'}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-ink-500">Fornecedor:</span> <strong>{nf.fornecedor_nome || '-'}</strong>
              {!nf.fornecedor_id && editavel && (nf.fornecedor_cnpj || nf.fornecedor_razao) && (
                <div className="mt-1 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">
                  Não cadastrado: <strong>{nf.fornecedor_razao || '—'}</strong>
                  {nf.fornecedor_cnpj && <span className="font-mono"> · {nf.fornecedor_cnpj}</span>}
                  <button
                    onClick={() => vincularFornecedor.mutate()}
                    disabled={vincularFornecedor.isPending}
                    className="ml-2 px-2 py-0.5 rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
                    {vincularFornecedor.isPending ? 'Vinculando…' : 'Cadastrar/vincular fornecedor'}
                  </button>
                </div>
              )}
            </div>
            <div><span className="text-ink-500">Status:</span> <strong>{nf.status}</strong></div>
            <div><span className="text-ink-500">Emissão:</span> {formatDateTime(nf.data_emissao)}</div>
            <div><span className="text-ink-500">Total:</span> <strong>{formatMoney(nf.valor_total)}</strong></div>
            <div className="col-span-2"><span className="text-ink-500">Chave:</span> <span className="font-mono text-xs">{nf.chave_acesso || '—'}</span></div>
            {nf.compra_pedido_id && <div><span className="text-ink-500">PC:</span> #{nf.compra_pedido_id}</div>}
            {nf.origem_venda_pedido_id && <div><span className="text-ink-500">PV origem intercompany:</span> #{nf.origem_venda_pedido_id}</div>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold uppercase">Itens — vínculo com o produto</div>
              {editavel && (
                <label className="text-xs flex items-center gap-1 text-ink-600" title="Grava o código do fornecedor no produto pra próxima NF entrar sozinha">
                  <input type="checkbox" checked={lembrar} onChange={e => setLembrar(e.target.checked)} />
                  Lembrar de-para do fornecedor
                </label>
              )}
            </div>
            {naoMapeados > 0 && editavel && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1 mb-2">
                ⚠ {naoMapeados} item(ns) sem produto vinculado — vincule todos antes de confirmar (senão não entram no estoque).
              </div>
            )}
            <table className="table text-xs">
              <thead><tr>
                <th>Item do XML</th><th className="text-right">Qtd</th>
                <th>Produto vinculado</th><th></th>
              </tr></thead>
              <tbody>
                {data?.itens?.map(it => (
                  <ItemMapeavel key={it.id} nf={nf} it={it} editavel={editavel}
                    lembrar={lembrar} onChanged={recarregar} />
                ))}
              </tbody>
            </table>
          </div>
          {nf.observacoes && (
            <div className="text-xs bg-ink-50 p-2 rounded">{nf.observacoes}</div>
          )}

          {/* Rateio entre filiais */}
          <div className="border-t pt-3">
            <button onClick={() => setShowRateio(s => !s)}
              className="text-xs font-semibold uppercase flex items-center gap-1 text-brand-700 hover:underline">
              🏢 Rateio entre filiais {showRateio ? '▼' : '▶'}
            </button>
            {showRateio && <RateioFiliaisNF nfId={nf.id} podeEditar={nf.status !== 'confirmada' && nf.status !== 'cancelada'} />}
          </div>
        </div>
      </div>
    </div>
  )
}

const ORIGEM_CHIP: Record<string, { label: string; cls: string }> = {
  de_para: { label: 'de-para', cls: 'bg-emerald-100 text-emerald-700' },
  ean: { label: 'EAN', cls: 'bg-emerald-100 text-emerald-700' },
  codigo: { label: 'código', cls: 'bg-emerald-100 text-emerald-700' },
  manual: { label: 'manual', cls: 'bg-blue-100 text-blue-700' },
  novo: { label: 'novo', cls: 'bg-violet-100 text-violet-700' },
}

function ItemMapeavel({ nf, it, editavel, lembrar, onChanged }: any) {
  const [busca, setBusca] = useState('')
  const [aberto, setAberto] = useState(false)
  const { data: opcoes = [] } = useQuery<any[]>({
    queryKey: ['prod-busca', busca],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: busca, limit: 20 } })).data,
    enabled: aberto && busca.length >= 2,
  })
  const mapear = useMutation({
    mutationFn: async (produto_id: number) => (await api.put(
      `/compras/nf-entradas/${nf.id}/itens/${it.id}`, { produto_id, lembrar })).data,
    onSuccess: () => { toast.success('Item vinculado'); setAberto(false); setBusca(''); onChanged() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao vincular'),
  })
  const criar = useMutation({
    mutationFn: async () => (await api.post(
      `/compras/nf-entradas/${nf.id}/itens/${it.id}/criar-produto`, { lembrar })).data,
    onSuccess: (r: any) => { toast.success(`Produto criado: ${r.codigo} — ${r.descricao}`); onChanged() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao criar produto'),
  })
  const chip = it.origem_match ? ORIGEM_CHIP[it.origem_match] : null
  return (
    <tr className="align-top">
      <td>
        <div>{it.descricao_fornecedor}</div>
        <div className="text-[10px] text-ink-400 font-mono">
          cód forn: {it.codigo_fornecedor || '—'}{it.ean_fornecedor ? ` · EAN ${it.ean_fornecedor}` : ''}
          {it.ncm ? ` · NCM ${it.ncm}` : ''}
        </div>
        {/* FIX 30/06: faltava o código do produto INTERNO na coluna "Item do XML".
            Antes só aparecia na coluna "Produto vinculado", obrigando a olhar pro outro
            lado da tabela pra ver o de-para. Agora mostra o cód interno na própria linha. */}
        {it.produto_id && it.produto?.codigo && (
          <div className="text-[10px] text-emerald-600 font-mono">
            cód produto: {it.produto.codigo}
          </div>
        )}
      </td>
      <td className="text-right whitespace-nowrap">
        {it.quantidade} {it.unidade_fornecedor || ''}
        {it.fator_conversao && it.fator_conversao !== 1 && (
          <div className="text-[10px] text-ink-400">→ {it.quantidade_interna} interna</div>
        )}
      </td>
      <td>
        {it.produto_id ? (
          <div className="flex items-center gap-1">
            <Check size={12} className="text-emerald-600" />
            <span className="font-mono">{it.produto?.codigo}</span>
            <span className="truncate max-w-[160px]">{it.produto?.descricao}</span>
            {chip && <span className={`text-[9px] px-1 rounded ${chip.cls}`}>{chip.label}</span>}
          </div>
        ) : editavel ? (
          <div className="space-y-1">
            {it.sugestao && (
              <button onClick={() => mapear.mutate(it.sugestao.produto_id)} disabled={mapear.isPending}
                className="text-[11px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded hover:bg-blue-100">
                Usar sugestão: <b>{it.sugestao.codigo}</b> {it.sugestao.descricao}
              </button>
            )}
            {!aberto ? (
              <div className="flex gap-1">
                <button onClick={() => setAberto(true)} className="text-[11px] text-brand-700 underline flex items-center gap-0.5">
                  <Search size={11} /> vincular produto
                </button>
                <button onClick={() => criar.mutate()} disabled={criar.isPending}
                  className="text-[11px] text-violet-700 underline flex items-center gap-0.5">
                  <Plus size={11} /> criar produto
                </button>
              </div>
            ) : (
              <div className="relative">
                <input autoFocus value={busca} onChange={e => setBusca(e.target.value)}
                  placeholder="Buscar produto…" className="input !py-1 !text-xs w-full" />
                {busca.length >= 2 && opcoes.length > 0 && (
                  <div className="absolute z-10 bg-white border rounded shadow max-h-48 overflow-auto w-full">
                    {opcoes.map((p: any) => (
                      <button key={p.id} onClick={() => mapear.mutate(p.id)}
                        className="block w-full text-left px-2 py-1 hover:bg-blue-50 text-[11px]">
                        <span className="font-mono">{p.codigo}</span> — {p.descricao}
                      </button>
                    ))}
                  </div>
                )}
                <button onClick={() => { setAberto(false); setBusca('') }} className="text-[10px] text-ink-400 mt-0.5">cancelar</button>
              </div>
            )}
          </div>
        ) : (
          <span className="text-ink-400">{it.codigo_fornecedor || '—'}</span>
        )}
      </td>
      <td className="text-right whitespace-nowrap text-[11px] text-ink-500">
        {formatMoney(it.valor_total)}
      </td>
    </tr>
  )
}

function RateioFiliaisNF({ nfId, podeEditar }: { nfId: number; podeEditar: boolean }) {
  const qc = useQueryClient()
  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-rateio'],
    queryFn: async () => (await api.get('/filiais')).data,
  })
  const { data: depositos = [] } = useQuery<any[]>({
    queryKey: ['depositos-rateio'],
    queryFn: async () => (await api.get('/estoque/depositos', { params: { todas_filiais: true } })).data,
  })
  const { data: rateios = [], refetch } = useQuery<any[]>({
    queryKey: ['nf-rateios', nfId],
    queryFn: async () => (await api.get(`/compras/nf-entradas/${nfId}/rateios`)).data,
  })
  const [linhas, setLinhas] = useState<{ filial_id: string; deposito_id: string; percentual: string }[]>([])
  useEffect(() => {
    if (rateios.length > 0) {
      setLinhas(rateios.map((r: any) => ({
        filial_id: String(r.filial_id),
        deposito_id: r.deposito_id ? String(r.deposito_id) : '',
        percentual: String(r.percentual),
      })))
    } else {
      setLinhas([])
    }
  }, [rateios.length])

  const adicionar = () => setLinhas(prev => [...prev, { filial_id: '', deposito_id: '', percentual: '0' }])
  const remover = (idx: number) => setLinhas(prev => prev.filter((_, i) => i !== idx))
  const set = (idx: number, k: string, v: string) => setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, [k]: v } : l))

  const totalPct = linhas.reduce((s, l) => s + (parseFloat(l.percentual) || 0), 0)
  const distribuirIgual = () => {
    if (linhas.length === 0) return
    const pct = (100 / linhas.length).toFixed(3)
    setLinhas(prev => prev.map(l => ({ ...l, percentual: pct })))
  }

  const salvar = useMutation({
    mutationFn: async () => (await api.put(`/compras/nf-entradas/${nfId}/rateios`, {
      rateios: linhas
        .filter(l => l.filial_id && parseFloat(l.percentual) > 0)
        .map(l => ({
          filial_id: parseInt(l.filial_id),
          deposito_id: l.deposito_id ? parseInt(l.deposito_id) : null,
          percentual: parseFloat(l.percentual),
        })),
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nf-rateios', nfId] })
      toast.success('Rateio salvo')
      refetch()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  if (filiais.length === 0) {
    return <div className="text-xs text-ink-500 mt-2 italic">Sem filiais cadastradas — rateio não aplicável.</div>
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-ink-600">
        Distribui o estoque desta NF entre filiais. Soma dos percentuais deve ser 100. Sem rateio: estoque vai pro depósito da NF.
      </p>
      {linhas.length === 0 ? (
        <div className="text-xs text-ink-500 italic">Sem rateio cadastrado.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left px-2 py-1">Filial</th>
              <th className="text-left px-2 py-1">Depósito</th>
              <th className="text-right px-2 py-1">% (0-100)</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => {
              const depositosFilial = depositos.filter((d: any) => d.filial_id === parseInt(l.filial_id) || !d.filial_id)
              return (
                <tr key={i} className="border-b">
                  <td className="px-2 py-1">
                    <select className="input !py-1 !text-xs" disabled={!podeEditar}
                      value={l.filial_id} onChange={e => set(i, 'filial_id', e.target.value)}>
                      <option value="">—</option>
                      {filiais.map(f => <option key={f.id} value={f.id}>{f.codigo} {f.nome_fantasia}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select className="input !py-1 !text-xs" disabled={!podeEditar}
                      value={l.deposito_id} onChange={e => set(i, 'deposito_id', e.target.value)}>
                      <option value="">— usa depósito da NF —</option>
                      {depositosFilial.map((d: any) => <option key={d.id} value={d.id}>{d.codigo} {d.nome}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" min="0" max="100" step="0.001"
                      className="input !py-1 !text-xs w-20 text-right" disabled={!podeEditar}
                      value={l.percentual} onChange={e => set(i, 'percentual', e.target.value)} />
                  </td>
                  <td className="px-2 py-1">
                    {podeEditar && (
                      <button onClick={() => remover(i)} className="text-rose-600 hover:bg-rose-50 p-0.5 rounded">×</button>
                    )}
                  </td>
                </tr>
              )
            })}
            <tr className={`font-semibold ${Math.abs(totalPct - 100) > 0.01 && linhas.length > 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
              <td colSpan={2} className="text-right px-2 py-1">Total</td>
              <td className="text-right px-2 py-1">{totalPct.toFixed(3)}%</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      )}
      {podeEditar && (
        <div className="flex gap-2">
          <button onClick={adicionar} className="btn-secondary text-xs">+ Filial</button>
          {linhas.length > 0 && (
            <button onClick={distribuirIgual} className="btn-secondary text-xs">Distribuir igual</button>
          )}
          <button onClick={() => salvar.mutate()}
            disabled={salvar.isPending || (linhas.length > 0 && Math.abs(totalPct - 100) > 0.01)}
            className="btn-primary text-xs">
            {salvar.isPending ? '...' : 'Salvar rateio'}
          </button>
        </div>
      )}
    </div>
  )
}

// ============================================================
// NOVA NF — dropdown de tipo + modal
// ============================================================

function NovaNFDropdown({ onPick }: { onPick: (tipo: 'produto' | 'imobilizado' | 'consumo' | 'servico' | 'avulsa') => void }) {
  const [open, setOpen] = useState(false)
  const tipos: { t: any; label: string; icon: any; desc: string }[] = [
    { t: 'avulsa', label: 'Entrada simples (sem nota)', icon: PackagePlus, desc: 'Mercadoria sem NF-e — gera estoque + contas a pagar' },
    { t: 'produto', label: 'NF de Produto', icon: ShoppingCart, desc: 'Mercadorias para revenda — entra no estoque, crédito ICMS' },
    { t: 'imobilizado', label: 'NF de Imobilizado', icon: Building2, desc: 'Ativo fixo — CIAP (ICMS em 48 parcelas)' },
    { t: 'consumo', label: 'NF de Consumo', icon: Cog, desc: 'Material de uso próprio — sem crédito ICMS' },
    { t: 'servico', label: 'NF de Serviço (NFS-e)', icon: Briefcase, desc: 'Serviços tomados com retenções IR/INSS/ISS' },
  ]
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="btn-primary text-sm">
        <Plus size={14} /> Nova NF ▾
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-80 bg-white rounded-lg border shadow-lg z-50">
            {tipos.map(t => {
              const I = t.icon
              return (
                <button key={t.t} onClick={() => { setOpen(false); onPick(t.t) }}
                  className="w-full flex items-start gap-2 p-3 hover:bg-ink-50 text-left border-b last:border-0">
                  <I size={16} className="text-brand-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">{t.label}</div>
                    <div className="text-xs text-ink-500">{t.desc}</div>
                  </div>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

interface Fornecedor { id: number; codigo: string | null; nome: string; documento: string | null }
interface Deposito { id: number; nome: string; is_default: boolean; ativo: boolean }

function NovaNFModal({ tipo, onClose, onDone }: {
  tipo: 'produto' | 'imobilizado' | 'consumo' | 'servico' | 'avulsa'
  onClose: () => void; onDone: () => void
}) {
  const [fornecedor_id, setFornecedorId] = useState<number | ''>('')
  const [deposito_id, setDepositoId] = useState<number | ''>('')
  const [numero_nf, setNumero] = useState('')
  const [serie, setSerie] = useState('1')
  const [chave, setChave] = useState('')
  const [data_emissao, setDataEmissao] = useState(new Date().toISOString().slice(0, 10))
  const [cfop, setCfop] = useState(
    tipo === 'produto' ? '1102' :
    tipo === 'imobilizado' ? '1551' :
    tipo === 'consumo' ? '1556' : ''
  )
  const [natureza, setNatureza] = useState(
    tipo === 'produto' ? 'Compra para comercialização' :
    tipo === 'imobilizado' ? 'Compra de ativo imobilizado' :
    tipo === 'consumo' ? 'Compra de material de uso e consumo' :
    'Serviço tomado'
  )
  // Impostos comuns
  const [valor_bc_icms, setBcIcms] = useState('0')
  const [valor_icms, setIcms] = useState('0')
  const [valor_ipi, setIpi] = useState('0')
  const [valor_icms_st, setIcmsSt] = useState('0')
  const [valor_pis, setPis] = useState('0')
  const [valor_cofins, setCofins] = useState('0')
  // Imobilizado
  const [descricao_bem, setDescBem] = useState('')
  const [vida_util_meses, setVidaUtil] = useState('48')
  const [gera_ciap, setGeraCiap] = useState(true)
  // Serviço
  const [valor_servicos, setValorServicos] = useState('0')
  const [valor_iss, setValorIss] = useState('0')
  const [valor_iss_retido, setIssRetido] = useState('0')
  const [valor_ir_retido, setIrRetido] = useState('0')
  const [valor_inss_retido, setInssRetido] = useState('0')
  const [valor_csll_retido, setCsllRetido] = useState('0')
  // Itens (produto/imobilizado/consumo)
  const [itens, setItens] = useState<Array<{ descricao_fornecedor: string; quantidade_fornecedor: string; valor_unitario_fornecedor: string; valor_total_item: string; ncm: string; cfop: string }>>([
    { descricao_fornecedor: '', quantidade_fornecedor: '1', valor_unitario_fornecedor: '0', valor_total_item: '0', ncm: '', cfop: '' },
  ])
  // Duplicatas
  const [duplicatas, setDuplicatas] = useState<Array<{ numero: string; data_vencimento: string; valor: string }>>([])
  const [observacoes, setObservacoes] = useState('')

  const { data: fornecedores = [] } = useQuery<Fornecedor[]>({
    queryKey: ['fornec-nf'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { limit: 500 } })).data,
  })
  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['dep-nf'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })

  const totalItens = itens.reduce((s, i) => s + (parseFloat(i.valor_total_item) || 0), 0)
  const totalGeral = tipo === 'servico'
    ? parseFloat(valor_servicos) || 0
    : totalItens + (parseFloat(valor_ipi) || 0) + (parseFloat(valor_icms_st) || 0)

  const criar = useMutation({
    mutationFn: async () => (await api.post('/compras/nf-entradas', {
      fornecedor_id: fornecedor_id || null,
      deposito_id: deposito_id || null,
      numero_nf: numero_nf || null,
      serie: serie || null,
      chave_acesso: chave || null,
      data_emissao: data_emissao || null,
      tipo_documento: tipo,
      cfop_principal: cfop || null,
      natureza_operacao: natureza || null,
      valor_bc_icms: parseFloat(valor_bc_icms) || 0,
      valor_icms: parseFloat(valor_icms) || 0,
      valor_ipi: parseFloat(valor_ipi) || 0,
      valor_icms_st: parseFloat(valor_icms_st) || 0,
      valor_pis: parseFloat(valor_pis) || 0,
      valor_cofins: parseFloat(valor_cofins) || 0,
      descricao_bem: descricao_bem || null,
      vida_util_meses: parseInt(vida_util_meses) || 48,
      gera_ciap: tipo === 'imobilizado' ? gera_ciap : false,
      valor_servicos: parseFloat(valor_servicos) || 0,
      valor_iss: parseFloat(valor_iss) || 0,
      valor_iss_retido: parseFloat(valor_iss_retido) || 0,
      valor_ir_retido: parseFloat(valor_ir_retido) || 0,
      valor_inss_retido: parseFloat(valor_inss_retido) || 0,
      valor_csll_retido: parseFloat(valor_csll_retido) || 0,
      itens: tipo === 'servico' ? [] : itens.map(it => ({
        descricao_fornecedor: it.descricao_fornecedor,
        quantidade_fornecedor: parseFloat(it.quantidade_fornecedor) || 0,
        valor_unitario_fornecedor: parseFloat(it.valor_unitario_fornecedor) || 0,
        valor_total_item: parseFloat(it.valor_total_item) || 0,
        ncm: it.ncm || null,
        cfop: it.cfop || null,
      })),
      duplicatas: duplicatas.map(d => ({
        numero: d.numero, data_vencimento: d.data_vencimento, valor: parseFloat(d.valor) || 0,
      })),
      observacoes: observacoes || null,
    })).data,
    onSuccess: () => { toast.success(`NF de ${tipo} criada (rascunho)`); onDone() },
  })

  const titulo: Record<string, string> = {
    produto: 'Nova NF de Produto',
    imobilizado: 'Nova NF de Imobilizado',
    consumo: 'Nova NF de Consumo',
    servico: 'Nova NF de Serviço (NFS-e)',
    avulsa: 'Entrada simples (sem nota)',
  }
  const ehAvulsa = tipo === 'avulsa'
  const mostrarItens = tipo !== 'servico'
  const mostrarServico = tipo === 'servico'
  const mostrarImobilizado = tipo === 'imobilizado'
  const mostrarEstoque = tipo === 'produto' || ehAvulsa
  const mostrarFiscal = !ehAvulsa && !mostrarServico  // chave/cfop/impostos só p/ NF de verdade

  const pronto = ehAvulsa
    ? itens.some(i => i.descricao_fornecedor && parseFloat(i.valor_total_item) > 0)
    : numero_nf && (tipo === 'servico' ? parseFloat(valor_servicos) > 0 : itens.some(i => i.descricao_fornecedor && parseFloat(i.valor_total_item) > 0))

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">{titulo[tipo]}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {ehAvulsa && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <PackagePlus size={16} className="mt-0.5 shrink-0" />
              <span>Entrada de mercadoria <strong>sem nota fiscal</strong>. Ao confirmar, <strong>dá entrada no estoque</strong> e gera <strong>contas a pagar</strong> (pelas duplicatas/parcelas). Sem NF-e, CFOP ou impostos.</span>
            </div>
          )}
          {/* Cabeçalho */}
          <div className="grid grid-cols-4 gap-3">
            <F label="Fornecedor *">
              <select className="input w-full" value={fornecedor_id}
                onChange={e => setFornecedorId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">—</option>
                {fornecedores.map(f => <option key={f.id} value={f.id}>{f.codigo ? f.codigo + ' - ' : ''}{f.nome}</option>)}
              </select>
            </F>
            {!ehAvulsa && (<>
              <F label="Nº NFe *">
                <input className="input w-full font-mono" value={numero_nf} onChange={e => setNumero(e.target.value)} />
              </F>
              <F label="Série">
                <input className="input w-full" value={serie} onChange={e => setSerie(e.target.value)} />
              </F>
            </>)}
            <F label={ehAvulsa ? 'Data da entrada' : 'Data emissão'}>
              <input type="date" className="input w-full" value={data_emissao} onChange={e => setDataEmissao(e.target.value)} />
            </F>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {mostrarFiscal && (<>
              <F label="Chave acesso (44)">
                <input className="input w-full font-mono text-xs" maxLength={44} value={chave} onChange={e => setChave(e.target.value)} />
              </F>
              <F label="CFOP">
                <input className="input w-full font-mono" maxLength={6} value={cfop} onChange={e => setCfop(e.target.value)} />
              </F>
              <F label="Natureza">
                <input className="input w-full" value={natureza} onChange={e => setNatureza(e.target.value)} />
              </F>
            </>)}
            {mostrarEstoque && (
              <F label="Depósito (entra estoque)">
                <select className="input w-full" value={deposito_id} onChange={e => setDepositoId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">— default —</option>
                  {depositos.filter(d => d.ativo).map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
                </select>
              </F>
            )}
          </div>

          {/* Campos de IMOBILIZADO */}
          {mostrarImobilizado && (
            <div className="border-l-4 border-brand-400 bg-brand-50 p-3 rounded space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Building2 size={14} /> Dados do bem imobilizado (CIAP)
              </div>
              <div className="grid grid-cols-3 gap-3">
                <F label="Descrição do bem *">
                  <input className="input w-full" value={descricao_bem} onChange={e => setDescBem(e.target.value)} placeholder="Ex: Prensa 500T" />
                </F>
                <F label="Vida útil (meses)">
                  <input type="number" className="input w-full" value={vida_util_meses} onChange={e => setVidaUtil(e.target.value)} />
                </F>
                <F label="">
                  <label className="flex items-center gap-2 text-sm pt-6">
                    <input type="checkbox" checked={gera_ciap} onChange={e => setGeraCiap(e.target.checked)} />
                    Gerar CIAP automaticamente ao confirmar
                  </label>
                </F>
              </div>
            </div>
          )}

          {/* Campos de SERVIÇO */}
          {mostrarServico && (
            <div className="border-l-4 border-violet-400 bg-violet-50 p-3 rounded space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Briefcase size={14} /> Dados do serviço e retenções
              </div>
              <div className="grid grid-cols-4 gap-3">
                <F label="Valor dos serviços *">
                  <input type="number" step="0.01" className="input w-full" value={valor_servicos} onChange={e => setValorServicos(e.target.value)} />
                </F>
                <F label="ISS">
                  <input type="number" step="0.01" className="input w-full" value={valor_iss} onChange={e => setValorIss(e.target.value)} />
                </F>
                <F label="ISS retido">
                  <input type="number" step="0.01" className="input w-full" value={valor_iss_retido} onChange={e => setIssRetido(e.target.value)} />
                </F>
                <F label="IR retido">
                  <input type="number" step="0.01" className="input w-full" value={valor_ir_retido} onChange={e => setIrRetido(e.target.value)} />
                </F>
                <F label="INSS retido">
                  <input type="number" step="0.01" className="input w-full" value={valor_inss_retido} onChange={e => setInssRetido(e.target.value)} />
                </F>
                <F label="CSLL retido">
                  <input type="number" step="0.01" className="input w-full" value={valor_csll_retido} onChange={e => setCsllRetido(e.target.value)} />
                </F>
                <F label="PIS retido">
                  <input type="number" step="0.01" className="input w-full" value={valor_pis} onChange={e => setPis(e.target.value)} />
                </F>
                <F label="COFINS retido">
                  <input type="number" step="0.01" className="input w-full" value={valor_cofins} onChange={e => setCofins(e.target.value)} />
                </F>
              </div>
            </div>
          )}

          {/* Impostos (produto/imobilizado/consumo — NÃO na entrada avulsa) */}
          {mostrarFiscal && (
            <div className="border-l-4 border-ink-300 bg-ink-50 p-3 rounded space-y-2">
              <div className="text-sm font-semibold">Impostos</div>
              <div className="grid grid-cols-6 gap-2">
                <F label="BC ICMS"><input type="number" step="0.01" className="input w-full text-sm" value={valor_bc_icms} onChange={e => setBcIcms(e.target.value)} /></F>
                <F label="ICMS"><input type="number" step="0.01" className="input w-full text-sm" value={valor_icms} onChange={e => setIcms(e.target.value)} /></F>
                <F label="IPI"><input type="number" step="0.01" className="input w-full text-sm" value={valor_ipi} onChange={e => setIpi(e.target.value)} /></F>
                <F label="ICMS ST"><input type="number" step="0.01" className="input w-full text-sm" value={valor_icms_st} onChange={e => setIcmsSt(e.target.value)} /></F>
                <F label="PIS"><input type="number" step="0.01" className="input w-full text-sm" value={valor_pis} onChange={e => setPis(e.target.value)} /></F>
                <F label="COFINS"><input type="number" step="0.01" className="input w-full text-sm" value={valor_cofins} onChange={e => setCofins(e.target.value)} /></F>
              </div>
            </div>
          )}

          {/* Itens */}
          {mostrarItens && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Itens</div>
                <button onClick={() => setItens([...itens, { descricao_fornecedor: '', quantidade_fornecedor: '1', valor_unitario_fornecedor: '0', valor_total_item: '0', ncm: '', cfop: '' }])}
                  className="btn-secondary !py-1 !px-2 text-xs"><Plus size={12} /> Item</button>
              </div>
              {itens.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end p-2 bg-ink-50 rounded">
                  <div className="col-span-4">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Descrição</div>
                    <input className="input w-full text-sm" value={it.descricao_fornecedor}
                      onChange={e => setItens(itens.map((x, i) => i === idx ? { ...x, descricao_fornecedor: e.target.value } : x))} />
                  </div>
                  <div className="col-span-1">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">NCM</div>
                    <input className="input w-full text-xs font-mono" value={it.ncm}
                      onChange={e => setItens(itens.map((x, i) => i === idx ? { ...x, ncm: e.target.value } : x))} />
                  </div>
                  <div className="col-span-1">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">CFOP</div>
                    <input className="input w-full text-xs font-mono" value={it.cfop}
                      onChange={e => setItens(itens.map((x, i) => i === idx ? { ...x, cfop: e.target.value } : x))} />
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Qtd</div>
                    <input type="number" step="0.001" className="input w-full text-sm" value={it.quantidade_fornecedor}
                      onChange={e => setItens(itens.map((x, i) => {
                        if (i !== idx) return x
                        const q = parseFloat(e.target.value) || 0
                        const p = parseFloat(x.valor_unitario_fornecedor) || 0
                        return { ...x, quantidade_fornecedor: e.target.value, valor_total_item: (q * p).toFixed(2) }
                      }))} />
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">R$ unit.</div>
                    <input type="number" step="0.0001" className="input w-full text-sm" value={it.valor_unitario_fornecedor}
                      onChange={e => setItens(itens.map((x, i) => {
                        if (i !== idx) return x
                        const q = parseFloat(x.quantidade_fornecedor) || 0
                        const p = parseFloat(e.target.value) || 0
                        return { ...x, valor_unitario_fornecedor: e.target.value, valor_total_item: (q * p).toFixed(2) }
                      }))} />
                  </div>
                  <div className="col-span-1">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Total</div>
                    <input type="number" step="0.01" className="input w-full text-sm" value={it.valor_total_item}
                      onChange={e => setItens(itens.map((x, i) => i === idx ? { ...x, valor_total_item: e.target.value } : x))} />
                  </div>
                  <div className="col-span-1 text-right">
                    {itens.length > 1 && (
                      <button onClick={() => setItens(itens.filter((_, i) => i !== idx))} className="text-rose-600"><X size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex justify-between text-sm"><span className="text-ink-600">Subtotal itens:</span><strong>{formatMoney(totalItens)}</strong></div>
            </div>
          )}

          {/* Duplicatas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Duplicatas / parcelas (contas a pagar)</div>
              <button onClick={() => setDuplicatas([...duplicatas, { numero: String(duplicatas.length + 1), data_vencimento: '', valor: '0' }])}
                className="btn-secondary !py-1 !px-2 text-xs"><Plus size={12} /> Duplicata</button>
            </div>
            {duplicatas.length === 0 && <div className="text-xs text-ink-500">Sem duplicatas — lançamento financeiro à vista ou sem obrigação.</div>}
            {duplicatas.map((d, idx) => (
              <div key={idx} className="grid grid-cols-8 gap-2 mb-2 items-end p-2 bg-ink-50 rounded">
                <div className="col-span-2">
                  <div className="text-[10px] uppercase text-ink-500 mb-0.5">Nº</div>
                  <input className="input w-full text-sm" value={d.numero}
                    onChange={e => setDuplicatas(duplicatas.map((x, i) => i === idx ? { ...x, numero: e.target.value } : x))} />
                </div>
                <div className="col-span-3">
                  <div className="text-[10px] uppercase text-ink-500 mb-0.5">Vencimento</div>
                  <input type="date" className="input w-full text-sm" value={d.data_vencimento}
                    onChange={e => setDuplicatas(duplicatas.map((x, i) => i === idx ? { ...x, data_vencimento: e.target.value } : x))} />
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase text-ink-500 mb-0.5">Valor</div>
                  <input type="number" step="0.01" className="input w-full text-sm" value={d.valor}
                    onChange={e => setDuplicatas(duplicatas.map((x, i) => i === idx ? { ...x, valor: e.target.value } : x))} />
                </div>
                <div className="col-span-1 text-right">
                  <button onClick={() => setDuplicatas(duplicatas.filter((_, i) => i !== idx))} className="text-rose-600"><X size={14} /></button>
                </div>
              </div>
            ))}
          </div>

          <F label="Observações">
            <textarea className="input w-full" rows={2} value={observacoes} onChange={e => setObservacoes(e.target.value)} />
          </F>

          <div className="bg-brand-50 border border-brand-200 p-3 rounded text-sm flex justify-between">
            <span>Total geral da NF:</span>
            <strong className="text-lg">{formatMoney(totalGeral)}</strong>
          </div>
        </div>

        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={!pronto || criar.isPending} onClick={() => criar.mutate()} className="btn-primary">
            {criar.isPending && <Loader2 size={14} className="animate-spin" />}
            Salvar rascunho
          </button>
        </div>
      </div>
    </div>
  )
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium mb-1">{label}</div>
      {children}
    </label>
  )
}

// ============================================================
// IMPORTAR XML NFe
// ============================================================

function ImportarXMLBotao({ onDone }: { onDone: () => void }) {
  const inputRef = (useState<HTMLInputElement | null>(null))[0]
  const qc = useQueryClient()
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('arquivo', file)
      const res = await fetch('/api/compras/nf-entradas/importar-xml', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('pain_token')}` },
        body: fd,
      })
      if (!res.ok) {
        const e = await res.json().catch(() => ({ detail: 'erro' }))
        throw new Error(e.detail || 'Falha ao importar')
      }
      return await res.json()
    },
    onSuccess: (r: any) => {
      const info = r.importado
      toast.success(
        `NF importada: ${info.itens_total} item(ns), ${info.itens_nao_mapeados} sem produto vinculado` +
        (info.fornecedor_encontrado ? '' : '. Fornecedor não cadastrado — preencha antes de confirmar.')
      )
      qc.invalidateQueries({ queryKey: ['nf-entradas'] })
      onDone()
    },
    onError: (e: Error) => toast.error(e.message),
  })
  return (
    <label className="btn-secondary text-sm cursor-pointer">
      📥 Importar XML
      <input type="file" accept=".xml,application/xml,text/xml"
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0]
          if (f) upload.mutate(f)
          e.target.value = ''  // permite reupload do mesmo arquivo
        }} />
    </label>
  )
}
